// src/modules/app-views/fiscalConfig/pages/Document/context/FiscalStateContext.jsx
// *******************************************************************************
// Context for sharing fiscal services enabled state across components
// Used to update CardHeader tags in real-time when switches are toggled
// *******************************************************************************
import { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';

// *******************************************************************************
// CONTEXT
// *******************************************************************************
const FiscalStateContext = createContext(null);

// *******************************************************************************
// PROVIDER
// *******************************************************************************
/**
 * FiscalStateProvider - Provides fiscal services enabled state to all children
 * @param {object} props.data - Document data to initialize state from
 * @param {ReactNode} props.children - Child components
 */
export const FiscalStateProvider = ({ data, children }) => {
  // Initialize state from data (saved values)
  const [fiscalEnabled, setFiscalEnabled] = useState({
    nfe: data?.fiscalData?.nfe?.isEnabled || false,
    nfce: data?.fiscalData?.nfce?.isEnabled || false,
    nfse: data?.fiscalData?.nfse?.isEnabled || false,
  });

  // Update a single service enabled state
  const setServiceEnabled = useCallback((service, enabled) => {
    setFiscalEnabled(prev => ({
      ...prev,
      [service]: enabled,
    }));
  }, []);

  // Sync state when data changes (e.g., after save/refresh)
  useEffect(() => {
    setFiscalEnabled({
      nfe: data?.fiscalData?.nfe?.isEnabled || false,
      nfce: data?.fiscalData?.nfce?.isEnabled || false,
      nfse: data?.fiscalData?.nfse?.isEnabled || false,
    });
  }, [data?.fiscalData?.nfe?.isEnabled, data?.fiscalData?.nfce?.isEnabled, data?.fiscalData?.nfse?.isEnabled]);

  const value = useMemo(
    () => ({
      fiscalEnabled,
      setServiceEnabled,
    }),
    [fiscalEnabled, setServiceEnabled]
  );

  return <FiscalStateContext.Provider value={value}>{children}</FiscalStateContext.Provider>;
};

// *******************************************************************************
// HOOK
// *******************************************************************************
/**
 * useFiscalState - Hook to access fiscal state context
 * @returns {{ fiscalEnabled: object, setServiceEnabled: function }}
 */
export const useFiscalState = () => {
  const context = useContext(FiscalStateContext);
  if (!context) {
    // Return default values if used outside provider (graceful fallback)
    return {
      fiscalEnabled: { nfe: false, nfce: false, nfse: false },
      setServiceEnabled: () => {},
    };
  }
  return context;
};

export default FiscalStateContext;
